import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/utils";
import React, { ChangeEvent, useRef } from "react";
import "./uploader.css";
import { Plus } from "@phosphor-icons/react";
import { Body } from "@hypercard/web-components";

interface DropzoneProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange"
  > {
  classNameWrapper?: string;
  className?: string;
  dropMessage: string;
  handleOnDrop: (acceptedFiles: FileList | null) => void;
  loading?: boolean;
  children?: React.ReactNode;
}

export const Uploader = React.forwardRef<HTMLDivElement, DropzoneProps>(
  (
    {
      className,
      classNameWrapper,
      dropMessage,
      handleOnDrop,
      loading,
      children,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const { files } = e.dataTransfer;
      if (inputRef.current) {
        inputRef.current.files = files;
        handleOnDrop(files);
      }
    };

    const handleButtonClick = () => {
      if (inputRef.current) {
        inputRef.current.click();
      }
    };

    return (
      <Card
        ref={ref}
        className={cn(
          "uploader-container h-[100px] rounded-none border-none bg-transparent hover:cursor-pointer",
          classNameWrapper,
        )}
      >
        <CardContent
          className="flex h-full flex-col items-center justify-center px-2 py-4 text-xs"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleButtonClick}
        >
          <div className="flex items-center justify-center ">
            {loading ? (
              <Body size="md" weight="medium">
                Uploading file...
              </Body>
            ) : (
              <>
                <Plus className="mr-3 size-6" />
                <Body size="md" weight="medium">
                  {dropMessage}
                </Body>
              </>
            )}
            <Input
              {...props}
              value={undefined}
              ref={inputRef}
              type="file"
              className={cn("hidden", className)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleOnDrop(e.target.files)
              }
            />
          </div>
          {children}
        </CardContent>
      </Card>
    );
  },
);
